import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, Trash2, Edit, CalendarIcon, FileSignature, MapPin, Loader2, CheckCircle, RotateCcw, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminAgreements = () => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAgreement, setEditingAgreement] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active"); // active, completed, all
  const [confirmDialog, setConfirmDialog] = useState({ open: false, agreement: null, action: null });
  const [formData, setFormData] = useState({
    a_number: "",
    project_name: "",
    location: "",
    start_date: null,
    end_date: null,
    notes: ""
  });

  const fetchAgreements = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/agreements?status=${statusFilter}`);
      setAgreements(response.data);
    } catch (error) {
      toast.error("Failed to load agreements");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchAgreements();
  }, [fetchAgreements]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      start_date: formData.start_date ? format(formData.start_date, "yyyy-MM-dd") : "",
      end_date: formData.end_date ? format(formData.end_date, "yyyy-MM-dd") : null
    };

    try {
      if (editingAgreement) {
        await axios.put(`${API}/agreements/${editingAgreement.id}`, payload);
        toast.success("Agreement updated successfully");
      } else {
        await axios.post(`${API}/agreements`, payload);
        toast.success("Agreement created successfully");
      }
      setShowDialog(false);
      resetForm();
      fetchAgreements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save agreement");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this agreement?")) return;
    try {
      await axios.delete(`${API}/agreements/${id}`);
      toast.success("Agreement deleted successfully");
      fetchAgreements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete agreement");
    }
  };

  const handleEdit = (agreement) => {
    setEditingAgreement(agreement);
    setFormData({
      a_number: agreement.a_number,
      project_name: agreement.project_name,
      location: agreement.location,
      start_date: agreement.start_date ? new Date(agreement.start_date) : null,
      end_date: agreement.end_date ? new Date(agreement.end_date) : null,
      notes: agreement.notes || ""
    });
    setShowDialog(true);
  };

  const handleComplete = async () => {
    const agreement = confirmDialog.agreement;
    try {
      await axios.put(`${API}/agreements/${agreement.id}/complete`);
      toast.success(`Agreement ${agreement.a_number} marked as Completed`);
      fetchAgreements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete agreement");
    } finally {
      setConfirmDialog({ open: false, agreement: null, action: null });
    }
  };

  const handleReopen = async () => {
    const agreement = confirmDialog.agreement;
    try {
      await axios.put(`${API}/agreements/${agreement.id}/reopen`);
      toast.success(`Agreement ${agreement.a_number} reopened`);
      fetchAgreements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reopen agreement");
    } finally {
      setConfirmDialog({ open: false, agreement: null, action: null });
    }
  };

  const resetForm = () => {
    setEditingAgreement(null);
    setFormData({
      a_number: "",
      project_name: "",
      location: "",
      start_date: null,
      end_date: null,
      notes: ""
    });
  };

  const isDateActive = (agreement) => {
    if (!agreement.end_date) return true;
    return new Date(agreement.end_date) >= new Date();
  };

  const getStatusBadge = (agreement) => {
    if (agreement.status === "completed") {
      return (
        <Badge className="bg-gray-200 text-gray-700">
          Completed
        </Badge>
      );
    }
    return (
      <Badge className={isDateActive(agreement) 
        ? "bg-green-100 text-green-800" 
        : "bg-amber-100 text-amber-700"
      }>
        {isDateActive(agreement) ? "Active" : "Date Ended"}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
      </div>
    );
  }

  return (
    <div data-testid="agreements-management">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <FileSignature className="w-8 h-8 text-[#F37021]" />
            <h1 className="text-4xl font-bold text-[#333333]">AGREEMENTS</h1>
          </div>
          <p className="text-[#52525B]">Manage A-numbers for worker requests</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]" data-testid="agreement-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#F37021] hover:bg-[#D95D15] text-white" data-testid="add-agreement-button">
                <Plus className="w-4 h-4 mr-2" />
                New Agreement
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>{editingAgreement ? "Edit Agreement" : "New Agreement"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="input-label">A-Number *</Label>
                  <Input
                    value={formData.a_number}
                    onChange={(e) => setFormData({ ...formData, a_number: e.target.value })}
                    placeholder="A-2024-001"
                    required
                    data-testid="agreement-a-number-input"
                  />
                </div>
                <div>
                  <Label className="input-label">Project Name *</Label>
                  <Input
                    value={formData.project_name}
                    onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                    placeholder="Stockholm Power Plant Maintenance"
                    required
                    data-testid="agreement-project-name-input"
                  />
                </div>
                <div>
                  <Label className="input-label">Location *</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Stockholm, Sweden"
                    required
                    data-testid="agreement-location-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="input-label">Start Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.start_date ? format(formData.start_date, "yyyy-MM-dd") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.start_date}
                          onSelect={(date) => setFormData({ ...formData, start_date: date })}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="input-label">End Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.end_date ? format(formData.end_date, "yyyy-MM-dd") : "Optional"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.end_date}
                          onSelect={(date) => setFormData({ ...formData, end_date: date })}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div>
                  <Label className="input-label">Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional information..."
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => {
                    setShowDialog(false);
                    resetForm();
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#F37021] hover:bg-[#D95D15] text-white">
                    {editingAgreement ? "Update" : "Create"} Agreement
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>A-NUMBER</TableHead>
                <TableHead>PROJECT NAME</TableHead>
                <TableHead>LOCATION</TableHead>
                <TableHead>DATES</TableHead>
                <TableHead>STATUS</TableHead>
                <TableHead className="text-right">ACTIONS</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agreements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    {statusFilter === "completed" 
                      ? "No completed agreements found." 
                      : statusFilter === "active"
                      ? "No active agreements found. Create your first A-number to get started."
                      : "No agreements found."
                    }
                  </TableCell>
                </TableRow>
              ) : (
                agreements.map((agreement) => (
                  <TableRow key={agreement.id} data-testid={`agreement-row-${agreement.a_number}`}>
                    <TableCell>
                      <span className="font-bold text-[#F37021]">{agreement.a_number}</span>
                    </TableCell>
                    <TableCell className="font-medium">{agreement.project_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-gray-600">
                        <MapPin className="w-4 h-4" />
                        {agreement.location}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{agreement.start_date}</p>
                        {agreement.end_date && (
                          <p className="text-gray-500">to {agreement.end_date}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(agreement)}
                      {agreement.status === "completed" && agreement.completed_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          {agreement.completed_at.split("T")[0]}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {agreement.status === "completed" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirmDialog({ open: true, agreement, action: "reopen" })}
                            className="text-blue-600 hover:text-blue-800"
                            data-testid={`reopen-agreement-${agreement.a_number}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Reopen
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirmDialog({ open: true, agreement, action: "complete" })}
                            className="text-gray-600 hover:text-gray-800"
                            data-testid={`complete-agreement-${agreement.a_number}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Complete
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(agreement)}
                          data-testid={`edit-agreement-${agreement.a_number}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(agreement.id)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-agreement-${agreement.a_number}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {confirmDialog.action === "complete" ? "Mark as Completed?" : "Reopen Agreement?"}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {confirmDialog.action === "complete" ? (
                <>
                  <strong>{confirmDialog.agreement?.a_number} — {confirmDialog.agreement?.project_name}</strong>
                  <br /><br />
                  Workers will no longer be able to log new hours against this project. 
                  Existing timesheets and requests will be preserved.
                </>
              ) : (
                <>
                  <strong>{confirmDialog.agreement?.a_number} — {confirmDialog.agreement?.project_name}</strong>
                  <br /><br />
                  This will reactivate the agreement. Workers who were previously assigned 
                  will be able to log hours again.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, agreement: null, action: null })}>
              Cancel
            </Button>
            <Button 
              onClick={confirmDialog.action === "complete" ? handleComplete : handleReopen}
              className={confirmDialog.action === "complete" 
                ? "bg-gray-600 hover:bg-gray-700" 
                : "bg-blue-600 hover:bg-blue-700"
              }
            >
              {confirmDialog.action === "complete" ? "Mark as Completed" : "Reopen Agreement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
